import {
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceProvider,
  getTabLabelForType,
  useMixpanel,
} from '@talos/kyoko';
import { memo, useCallback, useMemo } from 'react';
import {
  getDefaultPositionsTabProperties,
  isVisibleMobilePositionsTab,
  useDefaultPositionsTabs,
  useEnabledPositionsTabTypes,
} from '../../../Blotters/PositionsV3/defaults';
import {
  getPositionsTabTypeLabel,
  normalizePersistedPositionsTabLabel,
} from '../../../Blotters/PositionsV3/positionsTabLabels';
import { POSITIONS_V3_BLOTTER_PREFIX } from '../../../Blotters/PositionsV3/tokens';
import type {
  DerivativesPositionsTabProps,
  PositionsTabProps,
  SubAccountPositionsTabProps,
  UnifiedPositionsTabProps,
} from '../../../Blotters/PositionsV3/types';
import { TradingPlaceholder } from '../TradingPlaceholder';
import {
  MobileBlotterPanel,
  type MobileBlotterPanelAddTabOption,
  type MobileBlotterPanelProps,
  type MobileBlotterPanelRenderTabArgs,
} from './MobileBlotterPanel';
import { MobilePositionsDerivatives } from './Positions/MobilePositionsDerivatives';
import { MobilePositionsSubAccounts } from './Positions/MobilePositionsSubAccounts';
import { MobilePositionsUnified } from './Positions/MobilePositionsUnified';

/**
 * Mobile positions panel. Resolves the active tab and dispatches on
 * `tabType` to the matching mobile sub-blotter, mirroring desktop
 * `PositionsV3` routing. `blotterID` uses the same `principal/positions-v3/...`
 * convention as desktop so persisted layout / filter state is shared.
 */
export const MobilePositionsBlotter = memo(function MobilePositionsBlotter(
  props: Omit<MobileBlotterPanelProps<PositionsTabProps>, 'defaultTabs' | 'renderTab'>
) {
  const mixpanel = useMixpanel(MixpanelEventSource.PositionsBlotter);
  const defaultTabs = useDefaultPositionsTabs();
  const enabledTabTypes = useEnabledPositionsTabTypes();
  const isTabIncluded = useCallback(
    (tab: { tabType: string }) => isVisibleMobilePositionsTab(tab, enabledTabTypes),
    [enabledTabTypes]
  );
  const mobileTabTypeOptions = useMemo(
    () => enabledTabTypes.filter(tabType => tabType !== 'Predictions'),
    [enabledTabTypes]
  );

  const renderTab = useCallback(({ tab, tabId, updateTab }: MobileBlotterPanelRenderTabArgs<PositionsTabProps>) => {
    const blotterID = `${POSITIONS_V3_BLOTTER_PREFIX}/${tabId}`;
    switch (tab.tabType) {
      case 'Derivatives':
        return (
          <MobilePositionsDerivatives
            key={tabId}
            blotterID={blotterID}
            tab={tab as DerivativesPositionsTabProps}
            onUpdateTab={updatedTab => updateTab(updatedTab)}
          />
        );
      case 'Sub Accounts':
        return (
          <MobilePositionsSubAccounts
            key={tabId}
            blotterID={blotterID}
            tab={tab as SubAccountPositionsTabProps}
            onUpdateTab={updatedTab => updateTab(updatedTab)}
          />
        );
      case 'Unified':
        return (
          <MobilePositionsUnified
            key={tabId}
            blotterID={blotterID}
            tab={tab as UnifiedPositionsTabProps}
            onUpdateTab={updatedTab => updateTab(updatedTab)}
          />
        );
      case 'Predictions':
        return <TradingPlaceholder label={tab.label ?? 'Predictions'} />;
      default: {
        const _exhaustive: never = tab;
        return _exhaustive;
      }
    }
  }, []);

  const addTabOptions = useMemo<MobileBlotterPanelAddTabOption<PositionsTabProps>[]>(
    () =>
      mobileTabTypeOptions.map(tabType => ({
        id: tabType,
        label: getPositionsTabTypeLabel(tabType),
        icon: IconName.Positions,
        createTab: sourceTabs => {
          const newTab = getDefaultPositionsTabProperties(tabType);
          return {
            ...newTab,
            label: getTabLabelForType({
              tabs: [...sourceTabs],
              newTabType: tabType,
              typeKey: 'tabType',
              typeToLabel: getPositionsTabTypeLabel,
            }),
          };
        },
        onAdd: () =>
          mixpanel.track(MixpanelEvent.AddTab, {
            [MixpanelEventProperty.TabType]: tabType,
          }),
      })),
    [mixpanel, mobileTabTypeOptions]
  );

  return (
    <MixpanelSourceProvider value={MixpanelEventSource.PositionsBlotter}>
      <MobileBlotterPanel<PositionsTabProps>
        {...props}
        defaultTabs={defaultTabs}
        isTabIncluded={isTabIncluded}
        mapTab={normalizePersistedPositionsTabLabel}
        renderTab={renderTab}
        addTabOptions={addTabOptions}
      />
    </MixpanelSourceProvider>
  );
});
